/**
 * AttachmentButton — attach-source control for the chat composers.
 *
 * With `onSharePointItems`: a two-item source menu (local upload / SharePoint
 * attach picker, with the connect gate). Without it: the legacy plain paperclip
 * → hidden file input, unchanged from the original component.
 */

import { useRef, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SourceIcon } from '@/components/ui/source-icon'
import { SharePointBrowser, type SharePointStagedItem } from '@/components/SharePointBrowser'
import { useSharePointGate } from '@/hooks/useSharePointGate'
import { useSharePointStore } from '@/stores/sharepoint-store'
import { cn } from '@/lib/utils'
import {
  CHAT_ACCEPTED_FILE_TYPES,
  describeChatAttachmentIneligibility,
} from '@/lib/assistant-attachment-validation'
import { stashDraft } from '@/lib/draft-stash'

interface AttachmentButtonProps {
  onFiles: (files: File[]) => void
  disabled?: boolean
  /** When true, shows active/selected styling */
  active?: boolean
  /** Presence turns the paperclip into a source menu with the SharePoint option. */
  onSharePointItems?: (driveId: string, items: SharePointStagedItem[]) => void
  /** Attachment slots left in this composer — caps the picker's selection. */
  remainingSlots?: number
  /** Bytes left under the composer's total cap. */
  remainingBytes?: number
  /** Where to stash `draftText` before the OAuth redirect. See draft-stash.ts. */
  draftStashKey?: string
  draftText?: string
}

export function AttachmentButton({
  onFiles,
  disabled,
  active,
  onSharePointItems,
  remainingSlots,
  remainingBytes,
  draftStashKey,
  draftText,
}: AttachmentButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [sharePointOpen, setSharePointOpen] = useState(false)
  const { isConnected, ensureChecked, openOrConnect } = useSharePointGate()

  const acceptString = Object.entries(CHAT_ACCEPTED_FILE_TYPES)
    .flatMap(([mime, exts]) => [mime, ...exts])
    .join(',')

  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-7 text-xs",
        active
          ? "text-[hsl(var(--ai-accent))] hover:text-[hsl(var(--ai-accent))]"
          : "text-muted-foreground"
      )}
      disabled={disabled}
      {...(!onSharePointItems && { onClick: () => inputRef.current?.click() })}
      title="Attach files"
    >
      <Paperclip className="h-3.5 w-3.5" />
    </Button>
  )

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={acceptString}
      className="hidden"
      onChange={(e) => {
        const files = Array.from(e.target.files ?? [])
        if (files.length > 0) onFiles(files)
        // Reset so the same file can be selected again
        e.target.value = ''
      }}
    />
  )

  // Legacy surface — no SharePoint handler, no menu.
  if (!onSharePointItems) {
    return (
      <>
        {triggerButton}
        {hiddenInput}
      </>
    )
  }

  const handleSharePointClick = () => {
    // Live read, matching openOrConnect's own branch: stashing on the connected
    // path would leave a stale draft that gets restored on some later visit.
    if (draftStashKey && draftText && !useSharePointStore.getState().isConnected) {
      stashDraft(draftStashKey, draftText)
    }
    openOrConnect(() => setSharePointOpen(true))
  }

  return (
    <>
      {/* Connection state is resolved when the menu opens, not on mount — every
          composer renders this button, and none of them should probe on load. */}
      <DropdownMenu onOpenChange={(open) => { if (open) ensureChecked() }}>
        <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => inputRef.current?.click()}>
            <Paperclip className="h-4 w-4 mr-2 text-muted-foreground" />
            Upload from computer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSharePointClick}>
            <SourceIcon source="sharepoint" size={16} className="mr-2" />
            {isConnected ? 'From SharePoint' : 'Connect SharePoint'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {hiddenInput}
      {/* Lazy-mounted so the picker's cost lands only when it is asked for. */}
      {sharePointOpen && (
        <SharePointBrowser
          open={sharePointOpen}
          onClose={() => setSharePointOpen(false)}
          mode="pick"
          onStage={(selection) => onSharePointItems(selection.driveId, selection.items)}
          title="Attach from SharePoint"
          confirmLabel="Attach"
          pickConstraints={{
            validateItem: (item) => {
              const reason = describeChatAttachmentIneligibility(item.mimeType, item.size)
              return reason ? { valid: false, reason } : { valid: true }
            },
            ...(remainingSlots !== undefined && { maxSelection: remainingSlots }),
            ...(remainingBytes !== undefined && { maxTotalBytes: remainingBytes }),
            allowAlreadyImported: true,
          }}
        />
      )}
    </>
  )
}
